import React, { useState, useEffect } from 'react';
import { apiUtils } from '../../../utils/apiUtils';
import { Trash2, RefreshCw, AlertTriangle, Clock, Activity, CheckCircle, XCircle } from 'lucide-react';

const QueueManagement = () => {
	const [queueItems, setQueueItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [actionLoading, setActionLoading] = useState(new Set());

	const fetchQueueItems = async () => {
		try {
			const response = await apiUtils.get('/api/scans/queue/items');
			const data = await response.json();
			setQueueItems(data.items || []);
		} catch (err) {
			console.error('Error fetching queue items:', err);
			setError('Failed to fetch queue items');
		} finally {
			setLoading(false);
		}
	};

	const removeFromQueue = async (scanId) => {
		setActionLoading(prev => new Set(prev).add(scanId));

		try {
			await apiUtils.delete(`/api/scans/queue/${scanId}`);

			// Remove from local state
			setQueueItems(prev => prev.filter(item => item.scanId !== scanId));

		} catch (err) {
			console.error('Error removing scan from queue:', err);
			if (err.response?.data?.error) {
				setError(err.response.data.error);
			} else {
				setError('Failed to remove scan from queue');
			}
		} finally {
			setActionLoading(prev => {
				const newSet = new Set(prev);
				newSet.delete(scanId);
				return newSet;
			});
		}
	};

	const refresh = async () => {
		setLoading(true);
		setError(null);
		await fetchQueueItems();
	};

	useEffect(() => {
		fetchQueueItems();

		// Auto-refresh every 30 seconds
		const interval = setInterval(() => {
			if (!document.hidden) fetchQueueItems();
		}, 30000);

		return () => clearInterval(interval);
	}, []);

	const getStatusIcon = (status) => {
		switch (status) {
			case 'pending':
				return <Clock className="w-4 h-4 text-yellow-600" />;
			case 'processing':
				return <Activity className="w-4 h-4 text-brand-600" />;
			case 'completed':
				return <CheckCircle className="w-4 h-4 text-green-600" />;
			case 'failed':
				return <XCircle className="w-4 h-4 text-red-600" />;
			default:
				return <Clock className="w-4 h-4 text-muted-foreground" />;
		}
	};

	const getPriorityBadge = (priority) => {
		const priorityMap = {
			0: { label: 'Low', color: 'bg-muted text-foreground' },
			1: { label: 'Medium', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300' },
			2: { label: 'High', color: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300' }
		};

		const config = priorityMap[priority] || priorityMap[0];

		return (
			<span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
				{config.label}
			</span>
		);
	};

	const formatTimeAgo = (date) => {
		const now = new Date();
		const diff = now - date;
		const minutes = Math.floor(diff / 60000);
		const hours = Math.floor(minutes / 60);

		if (hours > 0) {
			return `${hours}h ${minutes % 60}m ago`;
		} else if (minutes > 0) {
			return `${minutes}m ago`;
		} else {
			return 'Just now';
		}
	};

	if (loading && queueItems.length === 0) {
		return (
			<div className="bg-card rounded-lg shadow-sm border border-border">
				<div className="p-6 border-b border-border">
					<h3 className="text-lg font-semibold text-foreground">Queue Management</h3>
				</div>
				<div className="p-6">
					<div className="flex items-center justify-center py-8">
						<RefreshCw className="w-6 h-6 animate-spin text-brand-600 mr-2" />
						<span className="text-muted-foreground">Loading queue items...</span>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-card rounded-lg shadow-sm border border-border">
			<div className="p-6 border-b border-border">
				<div className="flex items-center justify-between">
					<h3 className="text-lg font-semibold text-foreground">Queue Management</h3>
					<button
						onClick={refresh}
						disabled={loading}
						className="flex items-center space-x-2 bg-brand-600 text-white px-3 py-1 rounded-md hover:bg-brand-700 disabled:opacity-50"
					>
						<RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
						<span>Refresh</span>
					</button>
				</div>
			</div>

			<div className="p-6">
				{error && (
					<div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 dark:bg-red-500/10 dark:border-red-500/30">
						<div className="flex items-center">
							<AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
							<span className="text-red-800">{error}</span>
						</div>
					</div>
				)}

				{queueItems.length === 0 ? (
					<div className="text-center py-8">
						<Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
						<p className="text-muted-foreground text-lg">No scans in queue</p>
						<p className="text-muted-foreground text-sm">Queue is empty</p>
					</div>
				) : (
					<div className="space-y-4">
						{queueItems.map((item, index) => (
							<div
								key={item.id}
								className="flex items-center justify-between p-4 bg-muted rounded-lg hover:bg-muted transition-colors"
							>
								<div className="flex items-center space-x-4">
									<div className="flex items-center space-x-2">
										<span className="text-sm font-medium text-muted-foreground">#{index + 1}</span>
										{getStatusIcon(item.status)}
									</div>

									<div className="flex-1">
										<div className="flex items-center space-x-3">
											<h4 className="font-medium text-foreground">{item.clientName}</h4>
											<span className="text-xs bg-brand-100 text-brand-800 dark:bg-brand-500/15 dark:text-brand-300 px-2 py-1 rounded-full">
												{item.platform}
											</span>
											<span className="text-xs text-muted-foreground">
												@{item.accountHandle}
											</span>
											{getPriorityBadge(item.priority)}
										</div>
										<div className="flex items-center space-x-4 mt-1">
											<span className="text-sm text-muted-foreground">
												Created: {formatTimeAgo(new Date(item.createdAt))}
											</span>
											<span className="text-sm text-muted-foreground">
												Est. time: {item.estimatedTime}
											</span>
										</div>
									</div>
								</div>

								<div className="flex items-center space-x-2">
									<button
										onClick={() => removeFromQueue(item.scanId)}
										disabled={actionLoading.has(item.scanId)}
										className="flex items-center space-x-1 text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-1 rounded-md transition-colors disabled:opacity-50 dark:bg-red-500/10 dark:text-red-200"
									>
										{actionLoading.has(item.scanId) ? (
											<RefreshCw className="w-4 h-4 animate-spin" />
										) : (
											<Trash2 className="w-4 h-4" />
										)}
										<span className="text-sm">Remove</span>
									</button>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default QueueManagement;
